import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, clearJsonLd, setJsonLd, setSeo, track } from "@/lib/api";
import { GuideCard } from "@/components/site/Blocks";
import { CommercialModule } from "@/components/site/CommercialModule";
import { Loading } from "@/pages/NotFound";
import { SmartImage } from "@/components/site/SmartImage";
import { AffiliateCardLink, AffiliateCardButton } from "@/components/site/AffiliateCardLink";

const SLUG = "recreation";

const COMPONENTS = [
  ["Launch monitor", "Measures shot, club and ball data depending on the particular technology and model."],
  ["Impact screen", "A projection surface designed to receive golf-ball impacts."],
  ["Enclosure", "Provides structure for the screen and helps contain shots around the hitting area."],
  ["Projector", "Displays the simulator image. Installation requirements depend on screen dimensions, throw distance and projector specifications."],
  ["Hitting mat", "Provides the hitting surface and can vary substantially in size, construction and feel."],
  ["Simulator software", "Provides virtual courses, practice environments and other simulator experiences. Compatibility varies by launch monitor and software platform."],
];

export default function RecreationPage() {
  const [hub, setHub] = useState(null);
  const [offer, setOffer] = useState(null);
  // Autoplaying footage is suppressed for visitors who ask for reduced motion; they get the poster.
  const [motionOk] = useState(
    () => !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    api
      .get("/offers/resolve", { params: { category: "golf-simulators" } })
      .then((r) => setOffer(r.data.status === "active" ? r.data.offer : null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/hubs/${SLUG}`).then((r) => {
      setHub(r.data);
      setSeo({
        title: r.data.meta_title ||
          "Home Golf Simulators — Indoor Golf Simulator Systems | Human Home Living",
        description: r.data.meta_description ||
          "Compare home golf simulators and indoor golf simulator systems — complete packages, launch monitors, impact screens, enclosures, projectors and hitting mats. See what each part does, then find options that fit your room and budget.",
      });
      track({ event_type: "page_view", page: `/hub/${SLUG}` });
      const origin = window.location.origin;
      const url = `${origin}/hub/${SLUG}`;
      setJsonLd("ld-hub", {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: r.data.meta_title || r.data.name,
        description: r.data.meta_description || r.data.intro || "",
        url,
        isPartOf: { "@type": "WebSite", name: "Human Home Living", url: `${origin}/` },
      });
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: r.data.name, item: url },
        ],
      });
    }).catch(() => {});
    return () => {
      clearJsonLd("ld-hub");
      clearJsonLd("ld-breadcrumb");
    };
  }, []);

  if (!hub) return <Loading label="Loading golf simulators" />;

  const roomFit = (hub.tools || []).find((t) => t.slug === "golf-simulator-room-fit" && t.active !== false);

  return (
    <div data-testid="hub-page-recreation">
      <section className="relative h-[66vh] min-h-[520px] md:min-h-[600px] overflow-hidden">
        {/* The poster image stays mounted underneath: it is the fallback whenever the video is
            suppressed (reduced motion), still buffering, or unsupported. */}
        <SmartImage
          src={hub.image}
          alt="A home golf simulator set up in a garage"
          priority
          sizes="100vw"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {motionOk && (
          <video
            data-testid="recreation-hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            poster={hub.image}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
            aria-hidden="true"
            tabIndex={-1}
          >
            <source src="/media/golf-hero.webm" type="video/webm" />
            <source src="/media/golf-hero.mp4" type="video/mp4" />
          </video>
        )}
        {/* Only as much darkening as the lower-left type needs; the top two-thirds of the footage
            stays clear. */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-transparent to-transparent" />
        <div className="relative h-full max-w-[1280px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-14 md:pb-20 text-white">
          <nav className="overlay-copy text-[12.5px] font-medium text-white/90 mb-5">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span>Golf Simulators</span>
          </nav>
          <h1 className="overlay-title text-[40px] md:text-[58px] leading-[1.03] max-w-3xl">
            Home Golf Simulators
          </h1>
          {offer && (
            <div className="mt-8 md:mt-9">
              <a
                href={offer.tracking_url}
                target="_blank"
                rel="sponsored nofollow noopener"
                data-testid="recreation-hero-cta"
                onClick={() =>
                  track({
                    event_type: "cta_click",
                    cta_placement: "HERO CTA",
                    offer_category: "golf-simulators",
                    offer_id: offer.id,
                    merchant: offer.merchant,
                  })
                }
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-8 py-5 bg-sage text-white text-[13px] tracking-[0.13em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300 shadow-sm hover:shadow-md"
              >
                Explore Golf Simulators <ArrowRight size={17} />
              </a>
              <p className="overlay-copy text-[11.5px] text-white/70 mt-3.5 max-w-sm leading-relaxed">
                Affiliate link · opens at {offer.merchant}. We may earn a commission, at no extra
                cost to you.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl">
            <span className="eyebrow">About this category</span>
            <p className="text-[18px] md:text-[22px] font-light leading-[1.55] text-[#3F3F3F] mt-5">
              {hub.intro}
            </p>
          </div>

          <div className="mt-12 md:mt-16" data-testid="recreation-product-types">
            <span className="eyebrow">Explore golf simulator options</span>
            <div className="grid md:grid-cols-3 gap-px bg-line mt-5">
              {(hub.money_paths || []).map((m) => {
                const key = m.name.toLowerCase().replace(/[^a-z]+/g, "-");
                return (
                  <AffiliateCardLink
                    key={m.name}
                    href={offer?.tracking_url}
                    ariaLabel={`Shop ${m.name} at ${offer?.merchant || "our partner"}`}
                    testId={`product-type-${key}`}
                    trackPayload={{
                      event_type: "cta_click",
                      cta_placement: "CATEGORY CARD",
                      offer_category: "golf-simulators",
                      offer_id: offer?.id,
                      merchant: offer?.merchant,
                    }}
                    className={`bg-white flex flex-col ${offer ? "" : "pointer-events-none"}`}
                  >
                    {m.image && (
                      <div className="overflow-hidden aspect-[4/3] bg-bone">
                        <SmartImage
                          src={m.image}
                          alt={m.image_alt || m.name}
                          sizes="(min-width:768px) 33vw, 100vw"
                          maxWidth={1024}
                          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.045]"
                        />
                      </div>
                    )}
                    <div className="px-6 py-7">
                      <h3 className="text-[17.5px] font-medium text-charcoal">{m.name}</h3>
                      <p className="text-[16px] md:text-[17px] text-[#454545] mt-2 leading-relaxed">{m.note}</p>
                    </div>
                  </AffiliateCardLink>
                );
              })}
              {offer && (
                <AffiliateCardLink
                  href={offer.tracking_url}
                  ariaLabel={`Shop golf simulators at ${offer.merchant}`}
                  testId="product-type-shop-partner"
                  trackPayload={{
                    event_type: "cta_click",
                    cta_placement: "CATEGORY SHOP CARD",
                    offer_category: "golf-simulators",
                    offer_id: offer.id,
                    merchant: offer.merchant,
                  }}
                  className="bg-white flex flex-col"
                >
                  <div className="overflow-hidden aspect-[4/3] bg-bone">
                    <SmartImage
                      src="/api/assets/human-home-living/editorial/recreation/golf-setup.png"
                      alt="A hitting mat with a teed ball, a club and a launch monitor in front of an impact screen"
                      sizes="(min-width:768px) 33vw, 100vw"
                      maxWidth={1024}
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.045]"
                    />
                  </div>
                  <div className="px-6 py-7 flex flex-col flex-1">
                    <h3 className="text-[17.5px] font-medium text-charcoal">Shop Golf Simulators</h3>
                    <p className="text-[16px] md:text-[17px] text-[#454545] mt-2 leading-relaxed">
                      Explore complete golf simulator systems, launch monitors, screens, projectors,
                      and accessories at {offer.merchant}.
                    </p>
                    {/* Styled as a button but part of the single card link, so one click is one
                        navigation and one conversion. */}
                    <AffiliateCardButton
                      testId="recreation-shop-partner-cta"
                      className="mt-5 px-5 py-4 tracking-[0.12em]"
                    >
                      Shop {offer.merchant} <ArrowRight size={15} />
                    </AffiliateCardButton>
                  </div>
                </AffiliateCardLink>
              )}
            </div>
          </div>
        </div>
      </section>

      {roomFit && (
        <section className="bg-charcoal text-white px-5 md:px-8 py-16 md:py-20" data-testid="recreation-room-fit">
          <div className="max-w-[1280px] mx-auto max-w-[900px]">
            <span className="eyebrow eyebrow-light">Not sure what fits your room?</span>
            <h2 className="display text-[30px] md:text-[40px] leading-tight mt-4">{roomFit.name}</h2>
            <p className="text-[17px] md:text-[18px] leading-[1.6] text-[#CBC8C2] mt-4 max-w-2xl">
              Use your room dimensions to understand which golf simulator configurations may fit your
              available space. It is optional planning help, not a step you have to complete.
            </p>
            <Link
              to={`/tools/${roomFit.slug}`}
              data-testid="recreation-room-fit-cta"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-white text-charcoal text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-[#EDEAE4] transition-colors duration-300"
            >
              Check your room <ArrowRight size={15} />
            </Link>
          </div>
        </section>
      )}

      <section className="px-5 md:px-8 py-14 md:py-20" data-testid="recreation-components">
        <div className="max-w-[1280px] mx-auto">
          <span className="eyebrow">Understand the components</span>
          <h2 className="display text-[30px] md:text-[40px] leading-tight text-charcoal mt-4 max-w-2xl">
            What each part of a golf simulator does
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-line mt-9">
            {COMPONENTS.map(([name, note]) => (
              <div key={name} className="bg-white px-6 py-7">
                <h3 className="text-[17.5px] font-medium text-charcoal">{name}</h3>
                <p className="text-[16px] md:text-[17px] text-[#454545] mt-2 leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(hub.articles || []).length > 0 && (
        <section className="px-5 md:px-8 pb-16 md:pb-24" data-testid="recreation-guides">
          <div className="max-w-[1280px] mx-auto">
            <span className="eyebrow">Learn more about home golf simulators</span>
            <div
              data-testid="hub-articles"
              className={`grid gap-6 lg:gap-7 mt-8 ${
                hub.articles.length < 3 ? "md:grid-cols-2 max-w-3xl" : "md:grid-cols-2 lg:grid-cols-3"
              }`}
            >
              {hub.articles.map((a, i) => (
                <GuideCard key={a.slug} a={a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-5 md:px-8 pb-8">
        <div className="max-w-[1080px] mx-auto">
          <CommercialModule
            category="golf-simulators"
            label="Explore Indoor Golf Outlet"
            headline="Ready to look at golf simulator options?"
            note="Explore the options in this category whenever you're ready."
            allowLive
            testKey="recreation-final"
            placement="FINAL CTA"
          />
        </div>
      </section>
    </div>
  );
}
